import * as React from 'react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';

interface ControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: true;
}

export interface FormFieldProps {
  label: React.ReactNode;
  /** Подсказка под полем (text-2, 12px) — связана через aria-describedby. */
  hint?: React.ReactNode;
  /** Ошибка (neg, role=alert); поле получает aria-invalid. */
  error?: React.ReactNode;
  /** Пометка «обязательно» после подписи. */
  required?: boolean;
  /** Свой id поля; по умолчанию генерируется useId(). */
  id?: string;
  /** default — 13px text; filter — 12px text-2 (панель фильтров). */
  tone?: 'default' | 'filter';
  className?: string;
  /**
   * Поле: элемент (Input, Textarea, SelectTrigger, нативный select) — ему подставятся id
   * и aria-атрибуты; или функция (props) => ReactNode для составных контролов
   * (Radix <Select>: отдайте props в <SelectTrigger {...props}>).
   */
  children: React.ReactElement<Partial<ControlProps>> | ((props: ControlProps) => React.ReactNode);
}

/**
 * Подпись + поле + подсказка/ошибка с гарантированной связью label↔control (htmlFor/id).
 *
 *   <FormField label="Код" required hint="Латиница, цифры и _">
 *     <Input value={code} onChange={…} />
 *   </FormField>
 *
 *   <FormField label="Статус" tone="filter">
 *     {(p) => (
 *       <Select value={s} onValueChange={setS}>
 *         <SelectTrigger {...p}><SelectValue /></SelectTrigger>
 *         …
 *       </Select>
 *     )}
 *   </FormField>
 */
export function FormField({ label, hint, error, required, id, tone = 'default', className, children }: FormFieldProps) {
  const autoId = React.useId();
  const controlId = id ?? `f${autoId.replace(/:/g, '')}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-err` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const controlProps: ControlProps = {
    id: controlId,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(error ? { 'aria-invalid': true as const } : {}),
    ...(required ? { 'aria-required': true as const } : {}),
  };

  const control =
    typeof children === 'function'
      ? children(controlProps)
      : React.cloneElement(children, {
          ...controlProps,
          'aria-describedby':
            [children.props['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined,
        });

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <Label htmlFor={controlId} tone={tone}>
        {label}
        {/* Пробел нужен для доступного имени: иначе «Ценаобязательно» */}
        {required && <>{' '}<span className="ml-1 font-medium text-text-2">обязательно</span></>}
      </Label>
      {control}
      {error && (
        <span id={errorId} role="alert" className="text-[13px] font-semibold text-neg">
          {error}
        </span>
      )}
      {hint && (
        <span id={hintId} className="text-xs text-text-2">
          {hint}
        </span>
      )}
    </div>
  );
}
